import { RESEARCH_PAGE } from "./research";
import type {
  GlossaryPageContent,
  LibraryEntry,
  LibraryPageContent,
} from "./types";

/**
 * The resource library (/resources) and the glossary (/glossary).
 *
 * The library indexes things that exist. Palo Alto's equivalent page is mostly
 * gated ebooks and webinar replays; we have neither, and padding the shelf with
 * "coming soon" tiles would make the one genuinely strong asset — a decade's
 * worth of published, citable research — look like filler. So the index is
 * built from the research entries plus the open-source artifacts, and the
 * categories with nothing in them (case studies, webinars) are simply absent.
 *
 * Entries derive from RESEARCH_PAGE rather than being retyped, so a paper
 * cannot appear here with a different title or a stale link than it has on
 * /research.
 */

const RESEARCH_ENTRIES: LibraryEntry[] = RESEARCH_PAGE.lines.flatMap((line) =>
  line.publications.map((pub) => ({
    type: "papers",
    title: pub.title,
    blurb: pub.summary,
    href: pub.href,
    meta: pub.doi ? `${pub.venue} · ${pub.doi}` : pub.venue,
    external: true,
  })),
);

const BOOK_ENTRIES: LibraryEntry[] = RESEARCH_PAGE.books.items.map((book) => ({
  type: "books",
  title: book.title,
  blurb: book.summary,
  href: book.href,
  meta: book.isbn ? `${book.venue} · ISBN ${book.isbn}` : book.venue,
  external: true,
}));

const ARTIFACT_ENTRIES: LibraryEntry[] = RESEARCH_PAGE.artifacts.items.map(
  (item) => ({
    type: "code",
    title: item.name,
    blurb: item.body,
    href: item.href,
    meta: `Open source · ${item.license}`,
    external: true,
  }),
);

/**
 * Product evidence sections. These point inward at the pages that publish the
 * measured numbers with their methodology, rather than restating the figures
 * here where the qualifier would get separated from the claim.
 */
const EVIDENCE_ENTRIES: LibraryEntry[] = [
  {
    type: "evidence",
    title: "Cerberus: the validation set and what it does not claim",
    blurb:
      "The published harness results for Lethal Trifecta detection, including the observe-only measurement condition — the figures are a detection rate, not a blocking success rate.",
    href: "/cerberus#evidence",
    meta: "Product evidence",
  },
  {
    type: "evidence",
    title: "TraceLock: one control evaluation, every framework",
    blurb:
      "How a single control evaluation is reported against nine core frameworks and fifteen regulated framework packs through the crosswalk, and what an auditor can independently verify rather than take on trust.",
    href: "/tracelock#evidence",
    meta: "Product evidence",
  },
  {
    type: "evidence",
    title: "Argus: the agent kit and how it is delivered",
    blurb:
      "What the autonomous red-team engine covers, and why the full agent kit is a Warden engagement rather than software you install.",
    href: "/argus",
    meta: "Product evidence",
  },
];

const REFERENCE_ENTRIES: LibraryEntry[] = [
  {
    type: "reference",
    title: "Glossary",
    blurb:
      "The terms this site uses — lethal trifecta, blast radius, transitive taint, control crosswalk — defined once, with the source attributed where the term is not ours.",
    href: "/glossary",
    meta: "Reference",
  },
  {
    type: "reference",
    title: "Technical documentation",
    blurb:
      "Installation, API reference and deployment guides for the open cores, maintained alongside the code.",
    href: "/docs",
    meta: "Reference",
  },
];

export const LIBRARY_PAGE: LibraryPageContent = {
  path: "/resources",
  eyebrow: "Resources",
  headline: "Everything we have published, in one place",
  lede:
    "Papers with DOIs, books with ISBNs, open-source implementations you can run, and the evidence behind the numbers on the product pages. No gated downloads and no email wall.",
  filters: [
    { id: "papers", label: "Research papers" },
    { id: "books", label: "Books" },
    { id: "code", label: "Open source" },
    { id: "evidence", label: "Product evidence" },
    { id: "reference", label: "Reference" },
  ],
  entries: [
    ...RESEARCH_ENTRIES,
    ...BOOK_ENTRIES,
    ...ARTIFACT_ENTRIES,
    ...EVIDENCE_ENTRIES,
    ...REFERENCE_ENTRIES,
  ],
  close: {
    heading: "Want this applied to your own systems?",
    body:
      "Warden is the services organization — the engineers behind this research working on the agent estate you are already running.",
    ctas: [
      { label: "Request a briefing", href: "/contact?topic=resources" },
      { label: "Read the research", href: "/research" },
    ],
  },
};

/**
 * The glossary.
 *
 * Two rules. Terms we did not coin are attributed to whoever did — "lethal
 * trifecta" is Simon Willison's, and quietly adopting it as house vocabulary
 * would be a small theft that a technical reader will notice. And regulatory
 * entries describe what the instrument requires, never implying that Odingard
 * certifies or confers anything under it.
 */
export const GLOSSARY_PAGE: GlossaryPageContent = {
  path: "/glossary",
  eyebrow: "Resources",
  headline: "The vocabulary, defined once",
  lede:
    "Agentic security has more terminology than agreement. These are the definitions this site uses, with attribution where the term came from somewhere else.",
  terms: [
    {
      term: "Agentic AI",
      definition:
        "A system where a model does not just produce text but calls tools, reads and writes state, and takes actions with external effect. The security consequence is that a wrong output is no longer a wrong answer — it is an executed action.",
      seeAlso: [{ label: "AI Security", href: "/ai-security" }],
    },
    {
      term: "Agent red teaming",
      definition:
        "Adversarial testing aimed at an agent's actions rather than its answers: can it be induced to call a tool it should not, reach data outside its scope, or persist an instruction for later. Model-level jailbreak testing does not cover it.",
      seeAlso: [
        { label: "Agentic Red Teaming", href: "/warden/agentic-red-teaming" },
      ],
    },
    {
      term: "Lethal trifecta",
      definition:
        "The co-occurrence of three properties in one agent context: access to private data, exposure to untrusted content, and the ability to communicate externally. Any two are usually survivable; all three together mean an injected instruction can read secrets and send them out.",
      source:
        "Coined by Simon Willison, “The lethal trifecta for AI agents”, June 2025.",
      seeAlso: [{ label: "Cerberus", href: "/cerberus" }],
    },
    {
      term: "Indirect prompt injection",
      definition:
        "An instruction planted in content the agent reads — a web page, a document, a tool result — rather than typed by the user. The model has no reliable way to tell retrieved data from instruction, which is why filtering the output is the wrong control point.",
    },
    {
      term: "Tool boundary",
      definition:
        "The set of tools an agent can actually invoke and the arguments it is allowed to pass them. Reviewing it is usually the cheapest risk reduction available, because most agent deployments grant far more reach than the task needs.",
      seeAlso: [
        { label: "Tool-Boundary Review", href: "/warden/tool-boundary-review" },
      ],
    },
    {
      term: "Provenance ledger",
      definition:
        "A record of where each piece of data in an agent's context came from and what was derived from it. It is what makes contamination traceable after the fact rather than merely detectable at the moment of use.",
    },
    {
      term: "Transitive taint propagation",
      definition:
        "Tracking contamination through derived writes: an honest agent that reads a poisoned value and writes a conclusion based on it has propagated the taint in good faith. Without transitivity, containment stops at the first hop and the poison launders itself.",
      seeAlso: [{ label: "The research", href: "/research" }],
    },
    {
      term: "Blast radius, B(p)",
      definition:
        "The set of records reachable from a poisoned write p through the dependency graph — everything that would have to be quarantined to contain it. Computing it is what turns “something was poisoned” into a bounded, actionable remediation.",
    },
    {
      term: "Observe-only mode",
      definition:
        "A measurement condition in which a runtime guard records what it would have done without intervening. Detection figures gathered this way say nothing about whether an attack would have been blocked, and must not be reported as a blocking success rate.",
    },
    {
      term: "Source independence",
      definition:
        "Whether agreeing sources are distinct observations or echoes of a single origin. Consensus between dependent sources is not corroboration, and the difference is measurable from provenance alone.",
      seeAlso: [{ label: "The research", href: "/research" }],
    },
    {
      term: "Memory poisoning",
      definition:
        "Planting content in an agent's persistent memory so it influences a later, unrelated session. The delay is the point: the harmful action happens long after the injection, when nobody is looking at the session that caused it.",
      seeAlso: [{ label: "Mimir", href: "/mimir" }],
    },
    {
      term: "Continuous control monitoring",
      definition:
        "Evaluating security controls against live systems on a schedule instead of sampling them once a year for an audit. It changes compliance from a periodic reconstruction into a current statement of posture.",
      seeAlso: [{ label: "TraceLock", href: "/tracelock" }],
    },
    {
      term: "Control crosswalk",
      definition:
        "A mapping from one internal control to the requirements it satisfies across several frameworks, so a single evaluation can be reported against all of them rather than repeated per audit.",
      seeAlso: [
        {
          label: "Control Crosswalk Build-out",
          href: "/warden/crosswalk",
        },
      ],
    },
    {
      term: "AI inventory",
      definition:
        "The register of AI systems an organization actually operates, including the ones adopted without going through procurement. Nearly every AI governance obligation is per system, so nothing else can be assessed until this exists.",
      seeAlso: [{ label: "AI Inventory & Discovery", href: "/warden/ai-inventory" }],
    },
    {
      term: "EU AI Act",
      definition:
        "Regulation (EU) 2024/1689, which classifies AI systems by risk and attaches obligations to each class. The obligations land per system, which is why an inventory is the prerequisite to answering anything under it.",
      seeAlso: [{ label: "EU AI Act Readiness", href: "/warden/eu-ai-act" }],
    },
    {
      term: "ISO/IEC 42001",
      definition:
        "The management-system standard for AI. Certification against it is issued by an accredited certification body following an audit — a consultancy can prepare you for that audit but cannot confer the certificate.",
      seeAlso: [{ label: "ISO 42001 Alignment", href: "/warden/iso-42001" }],
    },
    {
      term: "NIST AI RMF",
      definition:
        "A voluntary risk-management framework organized around four functions: govern, map, measure and manage. NIST does not issue certification against it, so alignment is a claim about practice rather than a credential.",
      seeAlso: [{ label: "NIST AI RMF Alignment", href: "/warden/nist-ai-rmf" }],
    },
    {
      term: "Audit-grade evidence",
      definition:
        "Evidence an auditor can verify independently rather than accept on the strength of who produced it — timestamped, tamper-evident, and traceable back to the control evaluation that generated it.",
    },
    {
      term: "Governed autonomy",
      definition:
        "Letting agents act without supervision on each action, while the boundary they act within is defined, enforced and evidenced. The alternative positions are refusing autonomy or granting it unbounded, and neither survives contact with production.",
      seeAlso: [{ label: "Warden", href: "/warden" }],
    },
  ],
  close: {
    heading: "Missing a term?",
    body:
      "The glossary tracks the vocabulary this site actually uses. If something here is defined wrongly, or a term you needed is absent, tell us — it is a page we would rather have correct than complete.",
    ctas: [
      { label: "Get in touch", href: "/contact?topic=glossary" },
      { label: "Read the research", href: "/research" },
    ],
  },
};
